import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { LIST_MY_VIDEO_CONFERENCES, JOIN_VIDEO_CONFERENCE } from '../graphql/clientQueries';
import type { VideoConference, ChimeMeetingCredentials } from '../types';

interface ListMyVideoConferencesResponse {
  listMyVideoConferences: VideoConference[];
}

interface JoinVideoConferenceResponse {
  joinVideoConference: ChimeMeetingCredentials;
}

interface VideoConferencesSectionProps {
  maxItems?: number;
  showHeader?: boolean;
  className?: string;
}

export function VideoConferencesSection({ 
  maxItems = 3, 
  showHeader = true,
  className = ''
}: VideoConferencesSectionProps) {
  const [selectedConference, setSelectedConference] = useState<VideoConference | null>(null);
  const [waitingStatus, setWaitingStatus] = useState<{ conferenceId: string; message: string } | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const { data, loading } = useQuery<ListMyVideoConferencesResponse>(LIST_MY_VIDEO_CONFERENCES, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });

  const [joinConference, { loading: joining }] = useMutation<JoinVideoConferenceResponse>(JOIN_VIDEO_CONFERENCE);

  const conferences = data?.listMyVideoConferences || [];

  // Filter to show only upcoming (SCHEDULED) and active (IN_PROGRESS) conferences
  const activeConferences = conferences.filter(
    c => c.status === 'SCHEDULED' || c.status === 'IN_PROGRESS'
  );

  // Sort: IN_PROGRESS first, then by scheduled time
  const sortedConferences = [...activeConferences].sort((a, b) => {
    if (a.status === 'IN_PROGRESS' && b.status !== 'IN_PROGRESS') return -1;
    if (b.status === 'IN_PROGRESS' && a.status !== 'IN_PROGRESS') return 1;
    return new Date(a.scheduledStartTime).getTime() - new Date(b.scheduledStartTime).getTime();
  });

  const displayConferences = sortedConferences.slice(0, maxItems);

  // Poll for admission when in waiting room
  useEffect(() => {
    if (!waitingStatus || !isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const result = await joinConference({ variables: { id: waitingStatus.conferenceId } });
        const credentials = result.data?.joinVideoConference;
        
        if (credentials?.status === 'WAITING') {
          // Still waiting - update message
          setWaitingStatus({
            conferenceId: waitingStatus.conferenceId,
            message: credentials.message || 'Waiting for host...',
          });
        } else if (credentials?.meetingJson && credentials?.attendeeJson) {
          // Admitted! Open the video call
          setIsPolling(false);
          setWaitingStatus(null);
          setSelectedConference(null);
          
          // Open Chime meeting in new window or handle in-app
          // For now, show success and provide meeting info
          alert('You have been admitted to the meeting! The video call will open shortly.');
          // In a full implementation, you would navigate to a video call component
        }
      } catch (error: any) {
        const errorMessage = error.message || '';
        
        if (errorMessage.includes('cancelled') || errorMessage.includes('ended') || errorMessage.includes('COMPLETED')) {
          // Meeting was cancelled or ended, stop polling
          setIsPolling(false);
          setWaitingStatus(null);
          alert('The meeting has been cancelled or ended.');
        } else {
          console.error('Error polling for admission:', error);
        }
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [waitingStatus, isPolling, joinConference]);

  const handleJoin = useCallback(async (conference: VideoConference) => {
    try {
      const result = await joinConference({ variables: { id: conference.id } });
      const credentials = result.data?.joinVideoConference;
      
      if (credentials?.status === 'WAITING') {
        // User is in waiting room
        setWaitingStatus({
          conferenceId: conference.id,
          message: credentials.message || 'Waiting for host to admit you...',
        });
        setIsPolling(true);
      } else if (credentials?.meetingJson && credentials?.attendeeJson) {
        // User can join directly
        setSelectedConference(null);
        alert('Joining video conference... (In a full implementation, this would open the video call)');
        // In a full implementation, you would navigate to a video call component
      } else {
        alert('Unable to join the video conference. Please try again.');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Unable to join the video conference';
      
      // Handle specific error cases with user-friendly messages
      if (errorMessage.includes('cancelled')) {
        alert('This meeting has been cancelled.');
        setSelectedConference(null);
      } else if (errorMessage.includes('ended') || errorMessage.includes('COMPLETED')) {
        alert('This meeting has already ended.');
        setSelectedConference(null);
      } else {
        alert(errorMessage);
      }
    }
  }, [joinConference]);

  const handleCardClick = useCallback((conference: VideoConference) => {
    setSelectedConference(conference);
    setWaitingStatus(null);
    setIsPolling(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedConference(null);
    setWaitingStatus(null);
    setIsPolling(false);
  }, []);

  const handleLeaveWaitingRoom = useCallback(() => {
    setWaitingStatus(null);
    setIsPolling(false);
  }, []);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = new Date(now.getTime() + 86400000).toDateString() === date.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (isToday) return `Today, ${timeStr}`;
    if (isTomorrow) return `Tomorrow, ${timeStr}`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatFullDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getTimeUntilStart = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 0) return null;
    if (diffMins < 60) return `Starts in ${diffMins} min`;
    if (diffMins < 1440) return `Starts in ${Math.floor(diffMins / 60)} hr`;
    return null;
  };

  const isStartingSoon = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins >= 0 && diffMins <= 30;
  };

  // Don't render if no conferences
  if (!loading && displayConferences.length === 0) {
    return null;
  }

  return (
    <>
      <div className={className || 'mb-4'}>
        {showHeader && (
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="text-base font-semibold text-gray-900">Video Conferences</h3>
            </div>
            {activeConferences.length > maxItems && (
              <span className="text-sm text-gray-500">+{activeConferences.length - maxItems} more</span>
            )}
          </div>
        )}

        {loading && displayConferences.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Loading conferences...
          </div>
        ) : (
          <div className="space-y-2">
            {displayConferences.map((conference) => {
              const isInProgress = conference.status === 'IN_PROGRESS';
              const startingSoon = isStartingSoon(conference.scheduledStartTime);
              const timeUntil = getTimeUntilStart(conference.scheduledStartTime);

              return (
                <button
                  key={conference.id}
                  onClick={() => handleCardClick(conference)}
                  className={`w-full text-left p-3 rounded-lg border-l-4 transition-all hover:shadow-md ${
                    isInProgress 
                      ? 'bg-green-50 border-green-500 hover:bg-green-100' 
                      : startingSoon 
                      ? 'bg-amber-50 border-amber-500 hover:bg-amber-100' 
                      : 'bg-gray-50 border-blue-500 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 truncate">{conference.title}</h4>
                        {isInProgress && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                            LIVE
                          </span>
                        )}
                        {startingSoon && !isInProgress && (
                          <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded">
                            SOON
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{formatDateTime(conference.scheduledStartTime)}</span>
                        {timeUntil && (
                          <span className={`ml-1 ${startingSoon ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                            • {timeUntil}
                          </span>
                        )}
                      </div>

                      {conference.host && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Host: {conference.host.firstName} {conference.host.lastName}</span>
                        </div>
                      )}
                    </div>

                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Conference Detail Modal */}
      {selectedConference && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div
            className="bg-white rounded-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${
              selectedConference.status === 'IN_PROGRESS' ? 'bg-green-50' : 'bg-blue-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedConference.status === 'IN_PROGRESS' ? 'bg-green-500' : 'bg-blue-500'
                } text-white`}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Video Conference</h2>
                  {selectedConference.status === 'IN_PROGRESS' && (
                    <span className="text-xs text-green-600 font-medium">In Progress</span>
                  )}
                  {selectedConference.status === 'SCHEDULED' && (
                    <span className="text-xs text-blue-600 font-medium">Scheduled</span>
                  )}
                </div>
              </div>
              <button onClick={handleCloseModal} className="p-2 hover:bg-white/50 rounded-full transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Waiting Room Status */}
              {waitingStatus && waitingStatus.conferenceId === selectedConference.id && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800">
                        {waitingStatus.message.includes('not started') ? 'In Waiting Room' : 'Waiting Room'}
                      </p>
                      <p className="text-sm text-amber-600">{waitingStatus.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-amber-700 text-sm">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    <span className="ml-2">
                      {waitingStatus.message.includes('not started') 
                        ? 'Waiting for meeting to start...' 
                        : 'Waiting for host to admit you...'}
                    </span>
                  </div>
                  <button
                    onClick={handleLeaveWaitingRoom}
                    className="w-full mt-3 py-2 px-4 bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium rounded-lg transition-colors text-sm"
                  >
                    Leave Waiting Room
                  </button>
                </div>
              )}

              {/* Title */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-gray-900">{selectedConference.title}</h3>
              </div>

              {/* Details */}
              <div className="space-y-3">
                {/* Date & Time */}
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {formatFullDateTime(selectedConference.scheduledStartTime)}
                    </p>
                    <p className="text-sm text-gray-500">
                      Duration: {Math.round((new Date(selectedConference.scheduledEndTime).getTime() - new Date(selectedConference.scheduledStartTime).getTime()) / 60000)} minutes
                    </p>
                  </div>
                </div>

                {/* Host */}
                {selectedConference.host && (
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Host</p>
                      <p className="text-sm text-gray-500">
                        {selectedConference.host.firstName} {selectedConference.host.lastName}
                      </p>
                    </div>
                  </div>
                )}

                {/* Participants */}
                {selectedConference.participants && selectedConference.participants.length > 0 && (
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Participants</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedConference.participants.map((p, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Info */}
                {selectedConference.status === 'SCHEDULED' && !waitingStatus && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Note:</span> You can join the waiting room before the meeting starts. The host will admit you when the meeting begins.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 space-y-2">
              {!waitingStatus && (
                <button
                  onClick={() => handleJoin(selectedConference)}
                  disabled={joining}
                  className={`w-full py-3 px-4 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                    selectedConference.status === 'IN_PROGRESS'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {joining ? 'Joining...' : selectedConference.status === 'IN_PROGRESS' ? 'Join Meeting' : 'Join Waiting Room'}
                </button>
              )}
              <button
                onClick={handleCloseModal}
                className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                {waitingStatus ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
